import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IConnectionDetailsDTO, IConnectionGroupDTO, IMeetingConnectionRequestParams } from '../../../../../../../api/connections.dao';
import { IMeetingLinkDTO, IMeetingLinksDAO, MeetingLinkRole } from '../../../../../../../api/meeting-links.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { isValidAsShortPlainText } from '../../../../../../../infrastructure/utils/form-validation.utils';
import { getConnectionItemLabelText } from '../../../../../../../infrastructure/utils/connections.utils';
import { handleApiException } from '../../../../../../global/modal/alerts-host/alert-templates';
import {
    FIELD_IS_REQUIRED_ERROR_MESSAGE,
    SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE,
} from '../../../../../../../constants/validation-errors';
import { MeetingLinksViewModel } from '../meeting-links.viewmodel';

export type ConnectionPickerOption = {
    id: number;
    label: string;
    isGroup: boolean;
};

export type MeetingLinkInheritanceIds = {
    meetingPresenterRoleConnectionId?: number | null;
    meetingHostRoleConnectionId?: number | null;
    meetingSupportRoleConnectionId?: number | null;
    meetingDefaultConnectionId?: number | null;
    roomPresenterRoleConnectionId?: number | null;
    roomHostRoleConnectionId?: number | null;
    roomDefaultConnectionId?: number | null;
};

export type ManageMeetingLinkModalProps = {
    meetingApiParams: IMeetingConnectionRequestParams;
    connections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    inheritanceIds?: MeetingLinkInheritanceIds;
    inheritanceConnections?: (IConnectionDetailsDTO | IConnectionGroupDTO)[];
    onCreated?: (link: IMeetingLinkDTO, connectionType: string) => void;
    onUpdated?: (link: IMeetingLinkDTO, connectionType: string) => void;
    link?: IMeetingLinkDTO;
};

class ManageMeetingLinkModalViewModel {
    @mobx.observable
    public label = '';
    @mobx.observable
    public role: MeetingLinkRole | null = null;
    @mobx.observable
    public connectionId: number | null = null;
    @mobx.observable
    public connectionInherited = true;
    @mobx.observable
    public createPanelistLinkEnabled = false;
    @mobx.observable
    public saving = false;
    @mobx.observable
    public isSubmitted = false;

    constructor(
        private readonly props: ManageMeetingLinkModalProps,
        @IoC.Inject private readonly meetingLinksDAO: IMeetingLinksDAO = IoC.Container.get(IMeetingLinksDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider)
    ) {
        this.label = props.link?.label ?? '';
        this.role = props.link?.role ?? null;
        const inherited = Boolean(props.link) && props.link.connectionInherited === true;
        this.connectionInherited = props.link ? inherited : true;
        this.connectionId = inherited
            ? null
            : props.link?.connectionGroupId ?? props.link?.meetingConnectionId ?? null;
        this.createPanelistLinkEnabled = props.link?.createPanelistLinkEnabled ?? false;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isEditMode(): boolean {
        return Boolean(this.props.link);
    }

    public get connections(): IConnectionDetailsDTO[] {
        return this.props.connections;
    }

    public get connectionGroupsList(): IConnectionGroupDTO[] {
        return this.props.connectionGroups;
    }

    public get showConnectionField(): boolean {
        return this.role != null;
    }

    public get assignedConnectionId(): number | null {
        return this.connectionInherited ? null : this.connectionId;
    }

    public get derivedConnection(): IConnectionDetailsDTO | null {
        if (!this.showConnectionField || !this.connectionInherited) {
            return null;
        }
        return this.findConnection(this.resolveInheritedConnectionId());
    }

    public get connectionOptions(): ConnectionPickerOption[] {
        const connections = this.props.connections.map(connection => ({
            id: connection.meetingConnectionId,
            label: getConnectionItemLabelText(connection),
            isGroup: false,
        }));
        const groups = this.props.connectionGroups.map(group => ({
            id: group.meetingConnectionId,
            label: getConnectionItemLabelText(group),
            isGroup: true,
        }));
        return [...connections, ...groups];
    }

    public get labelErrorMessage(): string {
        return this.getLabelLengthError() || (this.showErrors() ? this.getLabelRequiredError() : '');
    }

    public get isFormValid(): boolean {
        return !this.getLabelRequiredError() && !this.getLabelLengthError();
    }

    public showErrors = (): boolean => {
        return this.isSubmitted;
    };

    private getLabelRequiredError(): string {
        if (!this.label.trim()) {
            return FIELD_IS_REQUIRED_ERROR_MESSAGE('Label', this.intl);
        }
        return '';
    }

    private getLabelLengthError(): string {
        if (!isValidAsShortPlainText(this.label)) {
            return SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE(this.intl);
        }
        return '';
    }

    @mobx.action
    public setLabel = (value: string): void => {
        this.label = value;
    };

    @mobx.action
    public setRole = (value: MeetingLinkRole | null): void => {
        this.role = value;
        // Role change always re-evaluates inheritance for the new role.
        this.connectionId = null;
        this.connectionInherited = true;
    };

    @mobx.action
    public setConnectionId = (value: number | null): void => {
        this.connectionId = value;
        this.connectionInherited = value == null;
    };

    @mobx.action
    public togglePanelistLinkCreationEnabled = (): void => {
        this.createPanelistLinkEnabled = !this.createPanelistLinkEnabled;
    };

    public submit = async (): Promise<boolean> => {
        mobx.runInAction(() => {
            this.isSubmitted = true;
        });
        if (!this.isFormValid || this.saving) {
            return false;
        }

        try {
            mobx.runInAction(() => {
                this.saving = true;
            });
            const inheriting = this.role != null && this.connectionInherited;
            const payload = {
                ...this.props.meetingApiParams,
                label: this.label.trim(),
                role: this.role,
                meetingConnectionId: inheriting ? null : this.connectionId,
                connectionInherited: inheriting,
                createPanelistLinkEnabled: this.createPanelistLinkEnabled,
            };
            if (this.props.link) {
                const updated = await this.meetingLinksDAO.updateMeetingLink({
                    ...payload,
                    meetingLinkId: this.props.link.meetingLinkId,
                });
                this.props.onUpdated?.(this.applyAssignment(updated), this.getConnectionType());
            } else {
                const created = await this.meetingLinksDAO.createMeetingLink(payload);
                this.props.onCreated?.(this.applyAssignment(created), this.getConnectionType());
            }
            return true;
        } catch (error) {
            handleApiException(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    private getConnectionType(): string {
        return MeetingLinksViewModel.getConnectionTypeForTracking(
            this.effectiveConnectionId(),
            this.props.connections,
            this.props.connectionGroups
        );
    }

    private effectiveConnectionId(): number | null {
        if (this.role != null && this.connectionInherited) {
            return this.resolveInheritedConnectionId();
        }
        return this.connectionId;
    }

    private resolveInheritedConnectionId(): number | null {
        const ids = this.props.inheritanceIds;
        if (!ids || !this.role) {
            return null;
        }
        const candidates = this.inheritanceCandidates(this.role, ids);
        return candidates.find(id => id != null && id !== 0) ?? null;
    }

    private inheritanceCandidates(role: MeetingLinkRole, ids: MeetingLinkInheritanceIds): (number | null | undefined)[] {
        if (role === MeetingLinkRole.Presenter) {
            return [
                ids.meetingPresenterRoleConnectionId,
                ids.roomPresenterRoleConnectionId,
                ids.meetingDefaultConnectionId,
                ids.roomDefaultConnectionId,
            ];
        }
        if (role === MeetingLinkRole.Host) {
            return [
                ids.meetingHostRoleConnectionId,
                ids.roomHostRoleConnectionId,
                ids.meetingDefaultConnectionId,
                ids.roomDefaultConnectionId,
            ];
        }
        return [
            ids.meetingSupportRoleConnectionId,
            ids.meetingDefaultConnectionId,
            ids.roomDefaultConnectionId,
        ];
    }

    private findConnection(connectionId: number | null): IConnectionDetailsDTO | null {
        if (connectionId == null) {
            return null;
        }
        const connection = this.props.connections.find(item => item.meetingConnectionId === connectionId);
        if (connection) {
            return connection;
        }
        const group = this.props.connectionGroups.find(item => item.meetingConnectionId === connectionId);
        if (group) {
            return group as unknown as IConnectionDetailsDTO;
        }
        const extra = this.props.inheritanceConnections?.find(item => item.meetingConnectionId === connectionId);
        return (extra as IConnectionDetailsDTO) ?? null;
    }

    private applyAssignment(link: IMeetingLinkDTO): IMeetingLinkDTO {
        const assignedId = this.effectiveConnectionId();
        const isGroupAssignment = assignedId != null
            && this.props.connectionGroups.some(group => group.meetingConnectionId === assignedId);
        return {
            ...link,
            connectionInherited: link.connectionInherited ?? (this.role != null && this.connectionInherited),
            meetingConnectionId: link.meetingConnectionId
                ?? (isGroupAssignment ? null : assignedId),
            connectionGroupId: link.connectionGroupId
                ?? (isGroupAssignment ? assignedId : link.connectionGroupId ?? null),
            createPanelistLinkEnabled: link.createPanelistLinkEnabled
                ?? this.createPanelistLinkEnabled,
        };
    }
}

export { ManageMeetingLinkModalViewModel };
